/**
 * JSON-LD for a mini-service page: `Product` + `AggregateOffer`, the same
 * entity pattern `/hh` verticals emit, so the site publishes one shape for
 * "a service with a price band" rather than two.
 *
 * Pure by design (mirrors `mini-vertical-breadcrumb.ts`): every rule in here
 * corresponds to a bug that shipped at least once, and a pure function is the
 * only place they can be pinned by a spec without a TestBed.
 *
 * What is deliberately NOT here:
 *
 * - `aggregateRating`. The previous `LocalBusiness` entity carried
 *   `ratingValue`/`reviewCount` derived from the profile star-ratings of
 *   bidders on ~13 vector-similar tasks, multiplied by a hardcoded 29
 *   (`mini-vertical-price-review.component.ts`) — 928 "reviews" on a service
 *   whose own `totalSimilarTasks` is 14. That is not a rating of this service,
 *   and publishing it as one is manual-action-eligible misrepresentation. No
 *   per-service review source exists today; when one does, it can be added
 *   here behind a test.
 * - The parent vertical's `displayPrice`. Wrong granularity: it aggregates a
 *   whole category, and the page never renders it.
 */

/**
 * Below this, a low price is a unit error, not a cheap job: the DK platform
 * minimum task budget is 205 kr and the NL minimum is EUR 30. The double `minorToMajor`
 * bug published `lowPrice: 2.05` on 166 of 166 `/hh` pages — this floor is
 * what turns a recurrence into a withheld node plus a server-side error
 * instead of published markup.
 */
export const MIN_PLAUSIBLE_LOW_PRICE: Record<string, number> = {
  DKK: 100,
  EUR: 15,
};

/** For currencies the table doesn't know. Never below the spec'd ≥50 check. */
const FALLBACK_FLOOR = 50;

export interface MiniVerticalProductSchemaInput {
  /** The specific service — `h1`, never a parent category field. */
  name: string;
  description: string;
  /** Absolute self-canonical URL. */
  url: string;
  image?: string;
  brandName: string;
  priceCurrency: string;
  /**
   * The band the page renders, in MAJOR units (kr / €): the trusted live band
   * when one exists, else `price_guide` — i.e. the component's resolved
   * `q25Price`/`q75Price` state, the same value the `<title>`, the meta
   * description and the visible widget quote. Pass `undefined` when the page
   * shows no band; the offers node is then omitted silently.
   */
  band?: { low: number; high: number };
  /**
   * `taskStatistics.averageOfferCount` — fractional. Omitted from the markup
   * when it rounds below 1: an `offerCount` of 0 is the defect that hit 50 of
   * 166 `/hh` pages, so absence is always preferred over a zero.
   */
  averageOfferCount?: number;
}

export interface MiniVerticalProductSchemaResult {
  schema: Record<string, unknown>;
  /**
   * Non-empty when price data was present but failed validation, and the
   * offers node was withheld because of it. A page with no band produces no
   * warning — only implausible data is loud.
   */
  warnings: string[];
}

const isUsablePrice = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export function buildMiniVerticalProductSchema(
  input: MiniVerticalProductSchemaInput,
): MiniVerticalProductSchemaResult {
  const warnings: string[] = [];

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    // The fragment makes the primary entity explicit — the reason the helper
    // LocalBusiness blocks were dropped is that nothing identified which
    // entity was the page's own.
    '@id': `${input.url}#product`,
    url: input.url,
    brand: { '@type': 'Brand', name: input.brandName },
  };

  // A Product without a name is an invalid rich-results item, and `""` is how
  // it would ship: the caller's fallback chain can bottom out empty. Loud,
  // because it means the document has no usable h1 — a data defect.
  const name = input.name?.trim();
  if (name) {
    schema['name'] = name;
  } else {
    warnings.push('name is empty — the Product node has no primary label');
  }

  // An absent description is merely a thinner node — omit silently.
  const description = input.description?.trim();
  if (description) {
    schema['description'] = description;
  }

  if (input.image) {
    schema['image'] = input.image;
  }

  if (input.band !== undefined) {
    const offers = buildAggregateOffer(input, warnings);
    if (offers) {
      schema['offers'] = offers;
    }
  }

  return { schema, warnings };
}

function buildAggregateOffer(
  input: MiniVerticalProductSchemaInput,
  warnings: string[],
): Record<string, unknown> | null {
  const { band } = input;
  if (!isUsablePrice(band?.low) || !isUsablePrice(band?.high)) {
    warnings.push(
      `band is not two positive finite numbers (${band?.low}–${band?.high}), offers withheld`,
    );
    return null;
  }

  // Normalize before the floor lookup — `dkk` would miss the table and get
  // the 50 fallback instead of the intended 100. A code that is not three
  // letters cannot be priced against any floor, so it withholds the node.
  const currency = (input.priceCurrency ?? '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) {
    warnings.push(
      `priceCurrency "${input.priceCurrency}" is not an ISO 4217 code, offers withheld`,
    );
    return null;
  }

  // Round before validating: `4.786940749697703` shipped once, and a floor
  // check on the raw value would let `99.6` round up past what was checked.
  const lowPrice = Math.round(band.low);
  const highPrice = Math.round(band.high);

  const floor = MIN_PLAUSIBLE_LOW_PRICE[currency] ?? FALLBACK_FLOOR;
  if (lowPrice < floor) {
    warnings.push(
      `lowPrice ${lowPrice} ${currency} is below the plausible floor of ${floor} — ` +
        `unit error or corrupt price_guide, offers withheld`,
    );
    return null;
  }
  if (lowPrice > highPrice) {
    warnings.push(
      `lowPrice ${lowPrice} > highPrice ${highPrice}, offers withheld`,
    );
    return null;
  }

  const offers: Record<string, unknown> = {
    '@type': 'AggregateOffer',
    priceCurrency: currency,
    lowPrice,
    highPrice,
    availability: 'https://schema.org/InStock',
    seller: { '@type': 'Organization', name: input.brandName },
  };

  const offerCount = Math.round(input.averageOfferCount ?? 0);
  if (offerCount >= 1) {
    offers['offerCount'] = offerCount;
  }

  return offers;
}
